import { Colors } from "../../utils/colors";
import PopupModalContainer, {
  PopupModalContainerWidth,
} from "../Containers/PopupModalContainer";
import RectangleButton from "../Buttons/RectangleButton";
import { useCallback, useState } from "react";
import NumberInput from "../NumberInput";
import { API } from "../../API";
import { getAccountState } from "../../redux/slices/accountSlice";
import { useSelector, useDispatch } from "react-redux";
import { HorizontalDivider } from "../Dividers/HorizontalDivider";
import { FormatCurrency } from "../../utils/FormatCurrency";
import SwitchToggleItem from "../SwitchToggleItem";
import AlertContainer from "../Containers/AlertContainer";
import ConfirmActionModal from "../Containers/ConfirmPopups/ConfirmActionModal";
import { showNotificationBanner } from "../../utils/notificationUtils";
import { NotificationType } from "@markit/common.types";
import { testStripe } from "../../utils/stripeAccountUtils";

type SendManualPayoutModalProps = {
  isVisible: boolean;
  setIsVisible: (isVisible: boolean) => void;
  availableBalance: number; // in cents
  updateBalance: (subtractAmount: number) => void; // to visually update the balance on main panel
};

const SendManualPayoutModal = (props: SendManualPayoutModalProps) => {
  const { isVisible, setIsVisible, availableBalance, updateBalance } = props;
  const { uid } = useSelector(getAccountState).account.accountData;
  const dispatch = useDispatch();
  const [payoutAmount, setPayoutAmount] = useState<number>(availableBalance); // in cents
  const [payoutType, setPayoutType] = useState<"full" | "exact" | "none">(
    "full"
  );
  const [loadingPayout, setLoadingPayout] = useState(false);
  const [alertText, setAlertText] = useState<string>("");
  const [confirmPayout, setConfirmPayout] = useState(false);

  const styles = {
    inputContainer: { padding: "10px 14px", borderRadius: 12, width: 100 },
  };

  const requestManualPayout = useCallback(async () => {
    setLoadingPayout(true);
    const { success } = await (testStripe(uid)
      ? API.testPayment.createManualStripePayout
      : API.payment.createManualStripePayout)({
      userId: uid,
      amount: payoutAmount,
    });
    setLoadingPayout(false);
    setIsVisible(false);
    updateBalance(payoutAmount);
    showNotificationBanner(
      dispatch,
      success ? "Payout Completed" : "Payout Failed",
      success ? NotificationType.AFFIRMATIVE : NotificationType.NEGATIVE
    );
  }, [uid, payoutAmount, setIsVisible, updateBalance, dispatch]);

  const confirmPayoutOnPress = useCallback(async () => {
    if (payoutAmount > availableBalance) {
      setAlertText("You cannot payout more than your available balance.");
      return;
    }
    setConfirmPayout(true);
  }, [availableBalance, payoutAmount]);

  return (
    <>
      <PopupModalContainer
        headerComp={
          <div className="ColumnNormal" style={{ gap: 4 }}>
            <span className="sectionTitle">Initiate a Manual Payout</span>
            <span className="smallBodySubtext" style={{ fontWeight: 400 }}>
              You are about to initiate a manual payout of your Stripe balance
              to your connected bank account. Manual payouts may take a few days
              to be reflected in your bank account.
            </span>
          </div>
        }
        valueComp={
          <div className="ColumnNormal" style={{ gap: 14 }}>
            <SwitchToggleItem
              title={`Full Payout (${FormatCurrency(availableBalance / 100)})`}
              description="Request a full payout of your available balance."
              toggleValue={payoutType === "full"}
              onChange={() => {
                setPayoutType(payoutType === "full" ? "none" : "full");
                setPayoutAmount(payoutType === "full" ? 0 : availableBalance);
              }}
              switchColor={Colors.BLUE5}
            />
            <SwitchToggleItem
              title="Exact Dollar Amount"
              description="Payout an exact dollar amount."
              toggleValue={payoutType === "exact"}
              onChange={() => {
                setPayoutType(payoutType === "exact" ? "none" : "exact");
                setPayoutAmount(0);
              }}
              switchColor={Colors.BLUE5}
            />
            {payoutType === "exact" ? (
              <div className="AlignedRowSpaced">
                <span className="bodyMedium">Dollar Amount</span>
                <NumberInput
                  min={0}
                  max={availableBalance}
                  prefix={"$"}
                  value={payoutAmount / 100}
                  onChange={(value) => {
                    const roundedValue = Number(value?.toFixed(2));
                    setPayoutAmount(roundedValue * 100);
                  }}
                  containerStyles={styles.inputContainer}
                  allowDecimal
                  altErrorMessage="Exceeds max balance"
                />
              </div>
            ) : null}
          </div>
        }
        fixedBottomContent={
          <div className="ColumnNormal">
            <HorizontalDivider altMargin={14} />
            <div className="RowNormal" style={{ gap: 14, paddingInline: 14 }}>
              <RectangleButton
                buttonLabel="Cancel"
                onPress={() => setIsVisible(false)}
                altTextColor={Colors.BLACK}
                altColor={Colors.WHITE1}
                containerStyles={{
                  padding: "14px 12px",
                  border: `0.5px solid ${Colors.GRAY2}`,
                }}
              />
              <RectangleButton
                buttonLabel={"Confirm Payout"}
                onPress={confirmPayoutOnPress}
                altTextColor={Colors.WHITE}
                altColor={Colors.BLACK}
                containerStyles={{ padding: "14px 12px" }}
                disabled={!payoutAmount || payoutAmount > availableBalance}
                loading={loadingPayout}
              />
            </div>
          </div>
        }
        noExit
        closeModal={() => setIsVisible(false)}
        hideModal={!isVisible}
        modalContentWidth={PopupModalContainerWidth.SMALL}
      />
      <AlertContainer
        headerComp={alertText}
        closeModal={() => setAlertText("")}
        hideModal={alertText === ""}
      />
      <ConfirmActionModal
        heading="Confirm Payout"
        subtext={`You are about to send a manual payout of ${FormatCurrency(
          payoutAmount / 100
        )} to your connected bank account. Manual payouts may take a few days to be reflected in your bank account.`}
        confirmButtonText="Confirm Payout"
        hideModal={!confirmPayout}
        setIsVisible={setConfirmPayout}
        confirmOnPress={requestManualPayout}
      />
    </>
  );
};

export default SendManualPayoutModal;
